import { device } from 'utils/device';
import { globals } from 'config/globals';
import { DOMHelper } from 'core/DOMHelper';
import { Events } from 'core/events/Events';
import { Simple } from 'utils/analytics';

export interface SlowFpsDialogDeps {
	/** Pause the active view controllers while the dialog is up */
	pause: () => void;
	/** Resume the active view controllers once the dialog is dismissed */
	unpause: () => void;
}

/**
 * The slow-frame-rate warning popup (DOM markup lives in
 * common/preloader.blade.php as .slow-fps-dialog). Owns the per-device copy,
 * the staged tween in/out, the close-button wiring, the show-once
 * localStorage flag and the globals.slowFpsShowing state. PixiController's
 * FPS watchdog decides WHEN to call show(); this class decides IF (at most
 * once per browser) and everything after.
 */
export class SlowFpsDialog {
	private _dom: DOMHelper;
	private _deps: SlowFpsDialogDeps;
	private _shown: boolean = false;

	constructor(dom: DOMHelper, deps: SlowFpsDialogDeps) {
		this._dom = dom;
		this._deps = deps;

		this._shown = localStorage.getItem('slow-popup-shown') === 'yes';
	}

	/** No-ops after the first show (per browser, via localStorage): */
	public show(): void {
		if (this._shown) {
			return;
		}

		this._shown = true;
		localStorage.setItem('slow-popup-shown', 'yes');

		globals.slowFpsShowing = true;

		let $popup = document.querySelector('.slow-fps-dialog') as HTMLElement;
		$popup.style.display = 'block';

		let copy;
		let isMobile = device.isMobile;
		let isSafari = device.isSafari;

		if (isMobile) {
			copy =
				'We have detected that you might be experiencing a slow frame rate. Please try turning off <strong>Low Power Mode</strong> if running. Otherwise please visit back later with newer device or computer.';
		} else if (isSafari) {
			copy =
				'We have detected that you might be experiencing a slow frame rate. You may have a better experience if you view the site with a different browser, such as  <strong>Chrome</strong> or <strong>Firefox</strong>. Or visit back later with a different device or computer.';
		} else {
			copy =
				'We have detected that you could be experiencing a slow frame rate. You may have a better experience by closing other applications and browser tabs, plugging-in your charger or visiting back later with newer device or computer.';
		}

		this._dom.setHTML($popup.querySelector('.slow-fps__copy'), copy);
		this._dom.setHTML($popup.querySelector('.slow-fps__button'), 'Continue');

		gsap.TweenMax.fromTo(
			$popup.querySelector('.slow-fps__image'),
			0.5,
			{ opacity: 0, y: 20 },
			{ opacity: 1, y: 0, delay: 0.5 }
		);
		gsap.TweenMax.fromTo(
			$popup.querySelector('.slow-fps__copy'),
			0.5,
			{ opacity: 0, y: 20 },
			{ opacity: 1, y: 0, delay: 0.8 }
		);
		gsap.TweenMax.fromTo(
			$popup.querySelector('.slow-fps__button'),
			0.5,
			{ opacity: 0, y: 20 },
			{ opacity: 1, y: 0, delay: 1.2 }
		);

		Events.on('click', $popup.querySelector('.slow-fps__button'), () => {
			this._hide();
			Simple.event('low_frame_popup_close_click ', { isMobile: isMobile, isSafari: isSafari });
		});

		this._deps.pause();

		Simple.event('low_frame_rate_start ', { isMobile: isMobile, isSafari: isSafari });
	}

	private _hide(): void {
		let $popup = document.querySelector<HTMLElement>('.slow-fps-dialog');

		gsap.TweenMax.to($popup.querySelector('.slow-fps__image'), 0.5, { opacity: 0, y: 20, delay: 0.2 });
		gsap.TweenMax.to($popup.querySelector('.slow-fps__copy'), 0.5, { opacity: 0, y: 20, delay: 0.1 });
		gsap.TweenMax.to($popup.querySelector('.slow-fps__button'), 0.5, { opacity: 0, y: 20, delay: 0 });

		setTimeout(() => {
			$popup.style.display = 'none';
			globals.slowFpsShowing = false;

			this._deps.unpause();
		}, 500);
	}
}
